import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { IdleWarningDialog } from '@/components/IdleWarningDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookie-based auth: always send the httpOnly session cookies.
axios.defaults.withCredentials = true;

const IDLE_LIMIT_MS = 60 * 60 * 1000; // 60 minutes of inactivity -> auto logout
const IDLE_WARNING_MS = 60 * 1000;    // warn during the final 60 seconds
const IDLE_STORAGE_KEY = 'supportiq_last_activity';

// Persisted last-activity so idle is honoured across reloads / tab reopen.
const readLastActivity = () => {
  const v = parseInt(localStorage.getItem(IDLE_STORAGE_KEY) || '', 10);
  return Number.isFinite(v) ? v : 0;
};
const writeLastActivity = (ts) => {
  try { localStorage.setItem(IDLE_STORAGE_KEY, String(ts)); } catch { /* ignore */ }
};
const clearLastActivity = () => {
  try { localStorage.removeItem(IDLE_STORAGE_KEY); } catch { /* ignore */ }
};
const isIdleExpired = () => {
  const last = readLastActivity();
  return last > 0 && Date.now() - last > IDLE_LIMIT_MS;
};

// ---- request timing beacon (kept from before) ----
axios.interceptors.request.use((config) => {
  config.metadata = { start: (typeof performance !== "undefined" ? performance.now() : Date.now()) };
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshing = useRef(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleRemaining, setIdleRemaining] = useState(0);
  const warningActiveRef = useRef(false);

  const markActivity = useCallback(() => {
    // While the warning prompt is up, ignore passive activity so the countdown
    // keeps running — the user must explicitly choose "Stay signed in".
    if (warningActiveRef.current) return;
    writeLastActivity(Date.now());
  }, []);

  const doLogout = useCallback(async () => {
    warningActiveRef.current = false;
    setShowIdleWarning(false);
    clearLastActivity();
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setUser(false);
  }, []);

  const stayActive = useCallback(async () => {
    warningActiveRef.current = false;
    setShowIdleWarning(false);
    writeLastActivity(Date.now());
    // Renew the session so the user genuinely stays signed in.
    try { await axios.post(`${API}/auth/refresh`); } catch { /* ignore */ }
  }, []);

  // ---- 401 -> silent refresh -> retry (single flight), but NEVER refresh once idle ----
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => {
        try {
          const start = response.config?.metadata?.start;
          const rid = response.headers?.["x-request-id"] || response.headers?.["X-Request-Id"];
          if (start && rid && response.config?.url) {
            const url = String(response.config.url);
            if (!url.includes("/client-duration")) {
              const now = typeof performance !== "undefined" ? performance.now() : Date.now();
              axios.post(`${API}/admin/activity/${rid}/client-duration`, { client_duration_ms: Math.max(0, Math.round(now - start)) }).catch(() => {});
            }
          }
        } catch { /* silent */ }
        return response;
      },
      async (error) => {
        const original = error.config || {};
        const url = String(original.url || "");
        const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") ||
          url.includes("/auth/verify-otp") || url.includes("/auth/logout");
        if (error.response?.status === 401 && !original._retry && !isAuthCall) {
          // If the user has been idle past the limit, do NOT silently refresh —
          // background polling must not be able to keep a stale session alive.
          if (isIdleExpired()) {
            doLogout();
            return Promise.reject(error);
          }
          original._retry = true;
          try {
            if (!refreshing.current) {
              refreshing.current = axios.post(`${API}/auth/refresh`).finally(() => { refreshing.current = null; });
            }
            await refreshing.current;
            return axios(original);
          } catch (e) {
            setUser(false);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [doLogout]);

  const checkAuth = useCallback(async () => {
    // Honour idle across reloads: if the last activity is older than the limit,
    // log out immediately instead of restoring the session.
    if (isIdleExpired()) {
      await doLogout();
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      markActivity();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [doLogout, markActivity]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // ---- idle handling: warn at 59 min, auto-logout at 60 min ----
  useEffect(() => {
    const authed = !!user && user !== false;
    if (!authed) return;
    const bump = () => markActivity();
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const timer = setInterval(() => {
      const last = readLastActivity();
      if (!last) return;
      const remaining = IDLE_LIMIT_MS - (Date.now() - last);
      if (remaining <= 0) {
        doLogout();
      } else if (remaining <= IDLE_WARNING_MS) {
        warningActiveRef.current = true;
        setShowIdleWarning(true);
        setIdleRemaining(Math.ceil(remaining / 1000));
      } else if (warningActiveRef.current) {
        warningActiveRef.current = false;
        setShowIdleWarning(false);
      }
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(timer);
    };
  }, [user, markActivity, doLogout]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    return data; // { otp_required, email }
  };

  const verifyOtp = async (email, code) => {
    const { data } = await axios.post(`${API}/auth/verify-otp`, { email, code });
    markActivity();
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (email) => {
    await axios.post(`${API}/auth/resend-otp`, { email });
  };

  const forgotPassword = async (email) => {
    await axios.post(`${API}/auth/forgot-password`, { email });
  };

  const changePassword = async (current_password, new_password) => {
    await axios.post(`${API}/auth/change-password`, { current_password, new_password });
  };

  const isAuthenticated = !!user && user !== false;
  const isAdmin = isAuthenticated && user.role === 'admin';
  const canEdit = isAuthenticated && (user.role === 'admin' || user.role === 'user');

  const value = {
    user: user === false ? null : user,
    loading,
    isAuthenticated,
    isAdmin,
    isViewer: false,
    canEdit,
    login,
    verifyOtp,
    resendOtp,
    forgotPassword,
    changePassword,
    logout: doLogout,
    checkAuth,
    stayActive,
    showIdleWarning,
    idleRemaining,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isAuthenticated && showIdleWarning && (
        <IdleWarningDialog seconds={idleRemaining} onStay={stayActive} onLogout={doLogout} />
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
